import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT } from '../legacy-ball/table-management/designTokens';

interface MemberInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  chapterAffiliation: string | null;
}

interface EmailUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function EmailUpgradeModal({ isOpen, onClose, onSuccess }: EmailUpgradeModalProps) {
  const [member, setMember] = useState<MemberInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [optInNotifications, setOptInNotifications] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    setEditingEmail(false);
    setCustomEmail('');
    setConfirmEmail('');
    apiClient<{ eligible: boolean; reason?: string; member?: MemberInfo }>('/email-upgrade/eligibility')
      .then((res) => {
        if (res.eligible && res.member) {
          setMember(res.member);
          setCustomEmail(res.member.email);
          setConfirmEmail(res.member.email);
        } else {
          setError(res.reason || 'Not eligible for email login upgrade.');
        }
      })
      .catch(() => setError('Unable to check eligibility. Please try again.'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const emailMismatch = customEmail.toLowerCase().trim() !== confirmEmail.toLowerCase().trim();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customEmail.trim());
  const canSubmit = !emailMismatch && emailValid && !submitting;

  const handleSubmit = async () => {
    if (!member || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient('/email-upgrade', {
        method: 'POST',
        body: {
          memberId: member.id,
          optInNotifications,
          email: customEmail.trim(),
        },
      });
      setSuccess(true);
    } catch (err: any) {
      const message = err?.message || 'Failed to submit upgrade request.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="email-upgrade-title">
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 id="email-upgrade-title" style={styles.title}>✉️ Upgrade to Email Login</h2>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close modal">✕</button>
        </div>

        {loading && (
          <div style={styles.body}>
            <p style={{ color: TEXT_MUTED, textAlign: 'center' }}>Checking eligibility...</p>
          </div>
        )}

        {!loading && error && !success && (
          <div style={styles.body}>
            <div style={styles.errorBox}>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>{error}</p>
            </div>
            <div style={styles.footer}>
              <button onClick={onClose} style={styles.secondaryBtn}>Close</button>
            </div>
          </div>
        )}

        {!loading && !error && member && !success && (
          <div style={styles.body}>
            <p style={{ fontSize: '0.85rem', color: TEXT_MUTED, marginBottom: '1rem' }}>
              Switch from ticket-number login to email login using your GA Chapter member credentials.
              Once approved, you'll log in with your email address instead of your ticket number.
            </p>

            <div style={styles.infoCard}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Name</span>
                <span style={styles.infoValue}>{member.firstName} {member.lastName}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Chapter</span>
                <span style={styles.infoValue}>{member.chapterAffiliation || 'Georgia'}</span>
              </div>
            </div>

            {/* Email entry with edit + confirm */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  Login Email Address
                </label>
                {!editingEmail && (
                  <button
                    onClick={() => setEditingEmail(true)}
                    style={{ background: 'none', border: 'none', fontSize: '0.75rem', color: PRIMARY, cursor: 'pointer', fontWeight: 600, padding: '2px 6px' }}
                    type="button"
                  >
                    ✏️ Change
                  </button>
                )}
              </div>

              {!editingEmail ? (
                <div style={{ fontSize: '0.88rem', fontWeight: 500, color: TEXT_DARK, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px' }}>
                  {customEmail}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <input
                    type="email"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="Enter email address"
                    style={styles.input}
                    aria-label="Email address"
                  />
                  <input
                    type="email"
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    placeholder="Confirm email address"
                    style={{
                      ...styles.input,
                      borderColor: confirmEmail && emailMismatch ? '#f87171' : BORDER_LIGHT,
                    }}
                    aria-label="Confirm email address"
                  />
                  {confirmEmail && emailMismatch && (
                    <span style={{ fontSize: '0.72rem', color: '#dc2626' }}>Email addresses do not match.</span>
                  )}
                  {customEmail && !emailValid && (
                    <span style={{ fontSize: '0.72rem', color: '#dc2626' }}>Please enter a valid email address.</span>
                  )}
                </div>
              )}
            </div>

            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={optInNotifications}
                onChange={(e) => setOptInNotifications(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontSize: '0.82rem', color: TEXT_DARK }}>
                Receive email notifications (announcements, reminders)
              </span>
            </label>

            <div style={styles.footer}>
              <button onClick={onClose} style={styles.secondaryBtn}>Cancel</button>
              <button onClick={handleSubmit} style={styles.primaryBtn} disabled={!canSubmit}>
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        )}

        {success && (
          <div style={styles.body}>
            <div style={styles.successBox}>
              <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>✅ Request Submitted!</p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: '#374151' }}>
                Your upgrade request has been sent to an administrator for review.
                You'll continue using your ticket number to log in until it's approved.
              </p>
            </div>
            <div style={styles.footer}>
              <button onClick={() => { onSuccess(); onClose(); }} style={styles.primaryBtn}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.25rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  title: {
    margin: 0,
    fontSize: '1.05rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.1rem',
    cursor: 'pointer',
    color: TEXT_MUTED,
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  body: {
    padding: '1.25rem',
  },
  infoCard: {
    backgroundColor: '#f9fafb',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    marginBottom: '1rem',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.4rem 0',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  infoLabel: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  infoValue: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: TEXT_DARK,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.25rem',
    cursor: 'pointer',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  primaryBtn: {
    padding: '0.5rem 1.25rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '0.5rem 1.25rem',
    backgroundColor: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    color: '#991b1b',
    marginBottom: '1rem',
  },
  successBox: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.85rem',
    color: TEXT_DARK,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  },
};
